import { AnalyticSearchRequestDto, ShipmentFiltersDto, ChargeFiltersDto } from 'src/generated/services/TFinancialApi';
import { ChargeTableFilters, InvoiceTableFilters, ShipmentTableFilters, UnifiedAnalyticsFilters } from './analyticsTableFilterTypes';

// Extended ShipmentFiltersDto to include chargeCountSet (added by backend, pending type regeneration)
type ExtendedShipmentFiltersDto = ShipmentFiltersDto & {
  chargeCountSet?: number[];
};

type ApplyFn = <T extends string>(key: string, tableValues?: T[], baseValues?: T[] | null) => T[] | null;

const intersectValues = <T extends string>(baseValues?: T[] | null, tableValues?: T[]) => {
  if (!tableValues || tableValues.length === 0) {
    return baseValues ?? null;
  }
  if (!baseValues || baseValues.length === 0) {
    return tableValues;
  }
  const baseSet = new Set(baseValues);
  return tableValues.filter((value) => baseSet.has(value));
};

const applyIntersect: ApplyFn = (_key, tableValues, baseValues) => intersectValues(baseValues, tableValues);

const applyWithAccessor =
  (accessorKey: string): ApplyFn =>
  (key, tableValues, baseValues) =>
    key === accessorKey ? null : intersectValues(baseValues, tableValues);

const resolveBilledEntity = (
  apply: ApplyFn,
  filterSearchRequest: AnalyticSearchRequestDto,
  nextRequest: AnalyticSearchRequestDto & { billedEntityCodeSet?: string[] | null },
  tableValues: string[]
) => {
  const billedEntityBase =
    (nextRequest as { billedEntityCodeSet?: string[] | null }).billedEntityCodeSet ?? (filterSearchRequest as { billedEntity?: string[] | null }).billedEntity;
  const billedEntity = apply('billedEntity', tableValues, billedEntityBase);
  if (billedEntity) {
    (nextRequest as { billedEntityCodeSet?: string[] | null }).billedEntityCodeSet = billedEntity;
  }
};

// Helper to convert charge count from string[] to number[]
const convertChargeCount = (chargeCount: string[] | null): number[] | undefined => {
  if (!chargeCount || chargeCount.length === 0) return undefined;
  const numericValues = chargeCount.map(Number).filter((n) => !Number.isNaN(n));
  return numericValues.length > 0 ? numericValues : undefined;
};

// Helper to build filters with conditional spreading (generic for any filter type)
// Only includes non-null/non-undefined values to avoid sending null fields
const buildFilters = <T extends Record<string, any>>(baseFilters: T | undefined, fields: Partial<T>): T => {
  const result: T = { ...baseFilters } as T;
  Object.entries(fields).forEach(([key, value]) => {
    if (value) {
      (result as any)[key] = value;
    }
  });
  return result;
};

// Helper to initialize request objects
const initializeRequest = (filterSearchRequest: AnalyticSearchRequestDto) => {
  const nextRequest = { ...filterSearchRequest } as AnalyticSearchRequestDto & {
    billedEntityCodeSet?: string[] | null;
    invoiceNumberSet?: string[] | null;
    invoiceReasonSet?: string[] | null;
  };
  const nextFilters = filterSearchRequest.filters ? { ...filterSearchRequest.filters } : undefined;
  const nextShipmentFilters = nextFilters?.shipmentFilters ? { ...nextFilters.shipmentFilters } : undefined;
  const nextChargeFilters = nextFilters?.chargeFilters ? { ...nextFilters.chargeFilters } : undefined;
  return { nextRequest, nextFilters, nextShipmentFilters, nextChargeFilters };
};

const buildInvoiceSearchRequest = (apply: ApplyFn, filterSearchRequest: AnalyticSearchRequestDto, tableFilters: InvoiceTableFilters) => {
  if (!filterSearchRequest) return filterSearchRequest;

  const { nextRequest, nextFilters, nextShipmentFilters } = initializeRequest(filterSearchRequest);

  // Invoice-specific fields
  nextRequest.fapRefSet = apply('fapRef', tableFilters.fapRef, filterSearchRequest.fapRefSet);
  nextRequest.accountNumberSet = apply('accountNumber', tableFilters.accountNumber, filterSearchRequest.accountNumberSet);
  nextRequest.invoiceStatusSet = apply('invoiceStatus', tableFilters.invoiceStatus, filterSearchRequest.invoiceStatusSet);
  nextRequest.invoiceNumberSet = apply('invoiceNumber', tableFilters.invoiceNumber, nextRequest.invoiceNumberSet);
  nextRequest.invoiceReasonSet = apply('reason', tableFilters.invoiceReason, nextRequest.invoiceReasonSet);

  resolveBilledEntity(apply, filterSearchRequest, nextRequest, tableFilters.billedEntity);

  // Cross-filter fields
  const shipmentNumber = apply('shipmentNumber', tableFilters.shipmentNumber, nextShipmentFilters?.shipmentNumberSet);
  const serviceProvider = apply('serviceProvider', tableFilters.serviceProviderCode, nextShipmentFilters?.serviceProviderCode);
  const modality = apply('modality', tableFilters.modality, nextShipmentFilters?.modality);

  // Invoice charge count filter -> goes to root level (same as invoiceStatusSet)
  // Use invoiceChargesCount first, fall back to legacy chargeCount
  const baseChargeCount = (nextRequest as any)?.chargeCountSet?.map(String) ?? null;
  const chargeCountValues = tableFilters.invoiceChargesCount || tableFilters.chargeCount;
  const chargeCount = apply('invoiceChargesCount', chargeCountValues, baseChargeCount);
  const chargeCountSet = convertChargeCount(chargeCount);

  // Set chargeCountSet at root level (same level as invoiceStatusSet)
  if (chargeCountSet) {
    (nextRequest as any).chargeCountSet = chargeCountSet;
  }

  const shipmentFilters = buildFilters<ExtendedShipmentFiltersDto>(nextShipmentFilters, {
    serviceProviderCode: serviceProvider,
    modality,
    shipmentNumberSet: shipmentNumber,
  });

  nextRequest.filters = {
    ...nextFilters,
    shipmentFilters,
  };

  return nextRequest;
};

const buildShipmentSearchRequest = (apply: ApplyFn, filterSearchRequest: AnalyticSearchRequestDto, tableFilters: ShipmentTableFilters) => {
  if (!filterSearchRequest) return filterSearchRequest;

  const { nextRequest, nextFilters, nextShipmentFilters } = initializeRequest(filterSearchRequest);

  // Cross-filter: invoiceNumber
  const invoiceNumber = apply('invoiceNumber', tableFilters.invoiceNumber, nextRequest.invoiceNumberSet);
  if (invoiceNumber) nextRequest.invoiceNumberSet = invoiceNumber;

  resolveBilledEntity(apply, filterSearchRequest, nextRequest, tableFilters.billedEntity);

  // Shipment charge count filter -> goes to filters.shipmentFilters.chargeCountSet
  // Use shipmentChargesCount first, fall back to legacy chargeCount or invoiceChargesCount
  const baseChargeCount = (nextShipmentFilters as any)?.chargeCountSet?.map(String) ?? null;
  const chargeCountValues = tableFilters.shipmentChargesCount || tableFilters.invoiceChargesCount || tableFilters.chargeCount;
  const chargeCount = apply('shipmentChargesCount', chargeCountValues, baseChargeCount);

  const shipmentFilters = buildFilters<ExtendedShipmentFiltersDto>(nextShipmentFilters, {
    shipmentNumberSet: apply('shipmentNumber', tableFilters.shipmentNumber, nextShipmentFilters?.shipmentNumberSet),
    serviceProviderCode: apply('serviceProvider', tableFilters.serviceProviderCode, nextShipmentFilters?.serviceProviderCode),
    modality: apply('modality', tableFilters.modality, nextShipmentFilters?.modality),
    vehicleID: apply('vehicleId', tableFilters.vehicleId, nextShipmentFilters?.vehicleID),
    serviceType: apply('serviceType', tableFilters.serviceType, nextShipmentFilters?.serviceType),
    mbl: apply('mbl', tableFilters.mbl, nextShipmentFilters?.mbl),
    shipperCodeSet: apply('shipper', tableFilters.shipperName, nextShipmentFilters?.shipperCodeSet),
    shipperCountrySet: apply('shipperCountry', tableFilters.shipperCountry, nextShipmentFilters?.shipperCountrySet),
    consigneeCodeSet: apply('consignee', tableFilters.consigneeName, nextShipmentFilters?.consigneeCodeSet),
    consigneeCountrySet: apply('consigneeCountry', tableFilters.consigneeCountry, nextShipmentFilters?.consigneeCountrySet),
    polSet: apply('pol', tableFilters.pol, nextShipmentFilters?.polSet),
    podSet: apply('pod', tableFilters.pod, nextShipmentFilters?.podSet),
    goodsNameSet: apply('goodsName', tableFilters.goodsName, nextShipmentFilters?.goodsNameSet),
    shipmentCurrentDeviationWorkflowSet: apply(
      'shipmentCurrentDeviationWorkflow',
      tableFilters.reviewStage,
      nextShipmentFilters?.shipmentCurrentDeviationWorkflowSet
    ),
    shipmentStatusSet: apply('shipmentStatus', tableFilters.shipmentStatus, nextShipmentFilters?.shipmentStatusSet),
    shipmentStatusReasonSet: apply('shipmentStatusReason', tableFilters.shipmentStatusReason, nextShipmentFilters?.shipmentStatusReasonSet),
    chargeCountSet: convertChargeCount(chargeCount),
  });

  nextRequest.filters = { ...nextFilters, shipmentFilters };
  return nextRequest;
};

const buildChargeSearchRequest = (apply: ApplyFn, filterSearchRequest: AnalyticSearchRequestDto, tableFilters: ChargeTableFilters) => {
  if (!filterSearchRequest) return filterSearchRequest;

  const { nextRequest, nextFilters, nextShipmentFilters, nextChargeFilters } = initializeRequest(filterSearchRequest);

  // Cross-filter: invoiceNumber
  const invoiceNumber = apply('invoiceNumber', tableFilters.invoiceNumber, nextRequest.invoiceNumberSet);
  if (invoiceNumber) nextRequest.invoiceNumberSet = invoiceNumber;

  resolveBilledEntity(apply, filterSearchRequest, nextRequest, tableFilters.billedEntity);

  // Charge table charge count filter (legacy, for backwards compatibility)
  const baseChargeCount = (nextShipmentFilters as any)?.chargeCountSet?.map(String) ?? null;
  const chargeCount = apply('chargeCount', tableFilters.chargeCount, baseChargeCount);

  const shipmentFilters = buildFilters<ExtendedShipmentFiltersDto>(nextShipmentFilters, {
    serviceProviderCode: apply('serviceProvider', tableFilters.serviceProviderCode, nextShipmentFilters?.serviceProviderCode),
    modality: apply('modality', tableFilters.modality, nextShipmentFilters?.modality),
    shipmentNumberSet: apply('shipmentNumber', tableFilters.shipmentNumber, nextShipmentFilters?.shipmentNumberSet),
    polSet: apply('pol', tableFilters.pol, nextShipmentFilters?.polSet),
    podSet: apply('pod', tableFilters.pod, nextShipmentFilters?.podSet),
    serviceType: apply('serviceType', tableFilters.serviceType, nextShipmentFilters?.serviceType),
    chargeCountSet: convertChargeCount(chargeCount),
  });

  // Charge filters
  const chargeFilters = buildFilters(nextChargeFilters, {
    chargeDomainSet: apply('chargeDomain', tableFilters.chargeDomain, nextChargeFilters?.chargeDomainSet),
    chargeNameSet: apply('chargeName', tableFilters.chargeName, nextChargeFilters?.chargeNameSet),
    legTypeSet: apply('legType', tableFilters.legType, nextChargeFilters?.legTypeSet),
    quantityUnitSet: apply('quantityUnit', tableFilters.quantityUnit, nextChargeFilters?.quantityUnitSet),
    chargeStatusSet: apply('chargeStatus', tableFilters.chargeStatus, nextChargeFilters?.chargeStatusSet),
    chargeStatusReasonSet: apply('chargeStatusReason', tableFilters.chargeStatusReason, nextChargeFilters?.chargeStatusReasonSet),
  });

  nextRequest.filters = { ...nextFilters, shipmentFilters, chargeFilters };
  return nextRequest;
};

export const buildInvoiceFilterSearchRequest = (accessorKey: string, filterSearchRequest: AnalyticSearchRequestDto, tableFilters: InvoiceTableFilters) =>
  buildInvoiceSearchRequest(applyWithAccessor(accessorKey), filterSearchRequest, tableFilters);

export const buildShipmentFilterSearchRequest = (accessorKey: string, filterSearchRequest: AnalyticSearchRequestDto, tableFilters: ShipmentTableFilters) =>
  buildShipmentSearchRequest(applyWithAccessor(accessorKey), filterSearchRequest, tableFilters);

export const buildChargeFilterSearchRequest = (accessorKey: string, filterSearchRequest: AnalyticSearchRequestDto, tableFilters: ChargeTableFilters) =>
  buildChargeSearchRequest(applyWithAccessor(accessorKey), filterSearchRequest, tableFilters);

export const buildInvoiceTableSearchRequest = (sanitizedFormValues: AnalyticSearchRequestDto, tableFilters: InvoiceTableFilters) =>
  buildInvoiceSearchRequest(applyIntersect, sanitizedFormValues, tableFilters);

export const buildShipmentTableSearchRequest = (sanitizedFormValues: AnalyticSearchRequestDto, tableFilters: ShipmentTableFilters) =>
  buildShipmentSearchRequest(applyIntersect, sanitizedFormValues, tableFilters);

export const buildChargeTableSearchRequest = (sanitizedFormValues: AnalyticSearchRequestDto, tableFilters: ChargeTableFilters) =>
  buildChargeSearchRequest(applyIntersect, sanitizedFormValues, tableFilters);

// Helper to merge table column filters (UnifiedAnalyticsFilters) into the base search request
export const mergeTableFiltersIntoRequest = (
  baseRequest: AnalyticSearchRequestDto,
  tableFilters: UnifiedAnalyticsFilters,
  context: 'invoice' | 'shipment' | 'charge' = 'invoice'
): AnalyticSearchRequestDto => {
  // Return as-is if no table filters or no active filters
  if (!tableFilters) {
    return baseRequest;
  }
  const hasActiveFilters = Object.values(tableFilters).some((arr) => Array.isArray(arr) && arr.length > 0);
  if (!hasActiveFilters) {
    return baseRequest;
  }

  // Helper to merge table filter with base filter (table filter takes precedence if non-empty)
  const baseShipmentFilters = baseRequest.filters?.shipmentFilters;
  const baseChargeFilters = baseRequest.filters?.chargeFilters;
  const baseTradeLaneFilters = baseRequest.filters?.tradeLaneFilters;

  // Build table-specific shipment filters (only non-empty values)
  const tableShipmentFilters: Partial<ExtendedShipmentFiltersDto> = {
    ...(tableFilters.shipmentNumber?.length > 0 && { shipmentNumberSet: tableFilters.shipmentNumber }),
    ...(tableFilters.mbl?.length > 0 && { mbl: tableFilters.mbl }),
    ...(tableFilters.serviceType?.length > 0 && { serviceType: tableFilters.serviceType }),
    ...(tableFilters.serviceProviderCode?.length > 0 && { serviceProviderCode: tableFilters.serviceProviderCode }),
    ...(tableFilters.modality?.length > 0 && { modality: tableFilters.modality }),
    ...(tableFilters.pol?.length > 0 && { polSet: tableFilters.pol }),
    ...(tableFilters.pod?.length > 0 && { podSet: tableFilters.pod }),
    ...(tableFilters.shipperName?.length > 0 && { shipperCodeSet: tableFilters.shipperName }),
    ...(tableFilters.shipperCountry?.length > 0 && { shipperCountrySet: tableFilters.shipperCountry }),
    ...(tableFilters.consigneeName?.length > 0 && { consigneeCodeSet: tableFilters.consigneeName }),
    ...(tableFilters.consigneeCountry?.length > 0 && { consigneeCountrySet: tableFilters.consigneeCountry }),
    ...(tableFilters.goodsName?.length > 0 && { goodsNameSet: tableFilters.goodsName }),
    ...(tableFilters.vehicleId?.length > 0 && { vehicleID: tableFilters.vehicleId }),
    ...(tableFilters.reviewStage?.length > 0 && { shipmentCurrentDeviationWorkflowSet: tableFilters.reviewStage }),
    ...(tableFilters.shipmentStatus?.length > 0 && { shipmentStatusSet: tableFilters.shipmentStatus }),
    ...(tableFilters.shipmentStatusReason?.length > 0 && { shipmentStatusReasonSet: tableFilters.shipmentStatusReason }),
    ...(tableFilters.shipmentChargesCount?.length > 0 && {
      chargeCountSet: tableFilters.shipmentChargesCount.map(Number).filter((n) => !Number.isNaN(n)),
    }),
  };

  // Build table-specific charge filters (only non-empty values)
  const tableChargeFilters: Partial<ChargeFiltersDto> = {
    ...(tableFilters.chargeDomain?.length > 0 && { chargeDomainSet: tableFilters.chargeDomain }),
    ...(tableFilters.chargeName?.length > 0 && { chargeNameSet: tableFilters.chargeName }),
    ...(tableFilters.legType?.length > 0 && { legTypeSet: tableFilters.legType }),
    ...(tableFilters.quantityUnit?.length > 0 && { quantityUnitSet: tableFilters.quantityUnit }),
    ...(tableFilters.chargeStatus?.length > 0 && { chargeStatusSet: tableFilters.chargeStatus }),
    ...(tableFilters.chargeStatusReason?.length > 0 && { chargeStatusReasonSet: tableFilters.chargeStatusReason }),
  };

  // Check if we have any table filters to add
  const hasTableShipmentFilters = Object.keys(tableShipmentFilters).length > 0;
  const hasTableChargeFilters = Object.keys(tableChargeFilters).length > 0;

  // Merge base filters with table filters only if table filters exist
  const finalShipmentFilters = hasTableShipmentFilters ? { ...baseShipmentFilters, ...tableShipmentFilters } : baseShipmentFilters;
  const finalChargeFilters = hasTableChargeFilters ? { ...baseChargeFilters, ...tableChargeFilters } : baseChargeFilters;

  return {
    ...baseRequest,
    filters: {
      ...baseRequest.filters,
      // Preserve tradeLaneFilters from base (drawer)
      ...(baseTradeLaneFilters && { tradeLaneFilters: baseTradeLaneFilters }),
      // Only include shipmentFilters if base has them OR table added some
      ...(finalShipmentFilters && { shipmentFilters: finalShipmentFilters }),
      // Only include chargeFilters if base has them OR table added some
      ...(finalChargeFilters && { chargeFilters: finalChargeFilters }),
    },
    // Invoice-specific filters (top-level) - table filter takes precedence if non-empty
    ...(tableFilters.fapRef?.length > 0 && { fapRefSet: tableFilters.fapRef }),
    ...(tableFilters.accountNumber?.length > 0 && { accountNumberSet: tableFilters.accountNumber }),
    ...(tableFilters.invoiceReason?.length > 0 && { invoiceReasonSet: tableFilters.invoiceReason }),
    ...(tableFilters.invoiceNumber?.length > 0 && { invoiceNumberSet: tableFilters.invoiceNumber }),
    ...(tableFilters.billedEntity?.length > 0 && { billedEntityCodeSet: tableFilters.billedEntity }),
    ...(tableFilters.invoiceStatus?.length > 0 && { invoiceStatusSet: tableFilters.invoiceStatus }),
    // Invoice chargeCount filter -> at root level (same level as invoiceStatusSet)
    ...((tableFilters.invoiceChargesCount?.length > 0 || tableFilters.chargeCount?.length > 0) && {
      chargeCountSet: (tableFilters.invoiceChargesCount?.length > 0 ? tableFilters.invoiceChargesCount : tableFilters.chargeCount)!
        .map(Number)
        .filter((n) => !Number.isNaN(n)),
    }),
  };
};
